import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * PIN kapısı — uygulama ancak sunucu "auth tamam" dedikten sonra mount edilir.
 *
 * Çerez HttpOnly olduğu için istemci "giriş yapılmış mı"yı KENDİSİ bilemez;
 * açılışta `/api/auth/check` sorulur. Sunucu auth'u kapalı tutuyorsa (UI_PIN
 * tanımsız — yerel geliştirme) kapı hiç görünmez.
 *
 * 6 hane dolunca kendiliğinden gönderilir — ayrıca "Giriş" düğmesi yok.
 */

const PIN_LENGTH = 6

type Phase = 'checking' | 'locked' | 'open'

export default function PinGate({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = useState<Phase>('checking')
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let iptal = false
    fetch('/api/auth/check')
      .then((r) => {
        if (iptal) return
        setPhase(r.ok ? 'open' : 'locked')
      })
      .catch(() => {
        // Sunucuya ulaşılamıyor — kapıyı gösterip mesajı orada verelim;
        // boş ekran en kötüsü.
        if (!iptal) {
          setPhase('locked')
          setError('Sunucuya ulaşılamadı — sayfayı yenileyin')
        }
      })
    return () => {
      iptal = true
    }
  }, [])

  const submit = useCallback(async (value: string) => {
    setBusy(true)
    setError(null)
    try {
      const r = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: value }),
      })
      if (r.ok) {
        setPhase('open')
        return
      }
      const body = await r.json().catch(() => null)
      setError(
        typeof body?.detail === 'string'
          ? body.detail
          : `Giriş başarısız (${r.status})`,
      )
      setPin('')
      inputRef.current?.focus()
    } catch {
      setError('Sunucuya ulaşılamadı')
    } finally {
      setBusy(false)
    }
  }, [])

  const onChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, PIN_LENGTH)
    setPin(digits)
    if (error) setError(null)
    if (digits.length === PIN_LENGTH) void submit(digits)
  }

  if (phase === 'open') return <>{children}</>
  if (phase === 'checking') return <div className="pin-screen" />

  return (
    <div className="pin-screen">
      <div className={`pin-card${error ? ' pin-card-error' : ''}`}>
        <div className="pin-brand">
          shadow<span>Trading</span>
        </div>
        <div className="pin-hint">Devam etmek için PIN girin</div>

        {/* Tek gizli input + üstünde hane kutuları: mobil klavye ve yapıştırma
            tek input'la sorunsuz, kutular yalnız görsel. */}
        <label className="pin-boxes">
          <input
            ref={inputRef}
            className="pin-input"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            autoFocus
            maxLength={PIN_LENGTH}
            value={pin}
            disabled={busy}
            onChange={(e) => onChange(e.target.value)}
            aria-label="PIN"
          />
          {Array.from({ length: PIN_LENGTH }, (_, i) => (
            <span
              key={i}
              className={`pin-box${i < pin.length ? ' pin-box-full' : ''}${
                i === pin.length && !busy ? ' pin-box-active' : ''
              }`}
            >
              {i < pin.length ? '•' : ''}
            </span>
          ))}
        </label>

        <div className="pin-status">
          {busy ? 'Doğrulanıyor…' : error ?? ' '}
        </div>
      </div>
    </div>
  )
}
